import { WebBodyText14, WebCaption12 } from '@fintronners/react-ui/src/GlobalStyling/webTypography';
import React from 'react';
import CurrencyInput from 'react-currency-input-field';
import { Controller, useFormContext, useFormState, useWatch } from 'react-hook-form';
import { TradeFormData, TradeSide } from '../schema';
import useTradeMinAmountLimit from '@fintronners/internal-core/src/trade/hooks/useTradeMinAmountLimit';
import { useTranslation } from 'react-i18next';
import { thousandFormatNumbroCurrency } from '@fintronners/react-utils/src/numberUtilsTSX';
import { ExclamationBubbleRed } from '@/assets/icons';
import Image from 'next/image';
import { QuantityType } from '@fintronners/internal-core/src/trade/constants';
import { QuantityTypeToggler } from '@/components/common/QuantityTypeToggler/QuantityTypeToggler';

type TradeCurrencyProps = {
  side: TradeSide;
  assetId?: string;
  isFractionable?: boolean;
  showUnitToggler?: boolean;
};

const stripLeadingZeros = (value: string | undefined) => value?.replace(/^0+(?=\d)/, '');

export const TradeCurrency = ({
  side,
  assetId,
  isFractionable,
  showUnitToggler,
}: TradeCurrencyProps) => {
  const { t } = useTranslation();
  const { control } = useFormContext<TradeFormData>();

  const [amount, shares, quantityType] = useWatch({
    control,
    name: ['amount', 'shares', 'quantityType'],
  });

  const isDollarsMode = quantityType === QuantityType.DOLLARS;
  const minimumAmount = useTradeMinAmountLimit(side, assetId, quantityType);

  return (
    <div>
      <WebBodyText14.Regular className="text-grey72">
        {t('miscScreens.tradeDisplay.tradeOrderEnterMinimumAmount', {
          minimum: isDollarsMode
            ? thousandFormatNumbroCurrency(minimumAmount, { mantissa: 0 })
            : minimumAmount,
        })}
      </WebBodyText14.Regular>

      <div className="flex items-center gap-4">
        <Controller
          control={control}
          key={quantityType}
          name={isDollarsMode ? 'amount' : 'shares'}
          render={({ field: { value, onChange } }) => (
            <div className="flex">
              <CurrencyInput
                className="w-full text-[60px] text-primary outline-none"
                value={value}
                defaultValue={isDollarsMode ? amount : shares}
                allowDecimals={isDollarsMode}
                decimalsLimit={2}
                intlConfig={{ locale: 'en-US' }}
                prefix={isDollarsMode ? '$' : ''}
                onValueChange={(_value) => onChange(stripLeadingZeros(_value) ?? '0')}
                allowNegativeValue={false}
              />
            </div>
          )}
        />

        {showUnitToggler && (
          <Controller
            control={control}
            name="quantityType"
            disabled={!isFractionable}
            render={({ field: { ref: _, ...field } }) => (
              <QuantityTypeToggler {...field} className="flex-shrink-0" />
            )}
          />
        )}
      </div>
    </div>
  );
};

export const TradeCurrencyError = () => {
  const { errors } = useFormState<TradeFormData>();
  const tradeAmountErrorMessage = errors.amount?.message;

  return (
    // We intentionally keep the wrapper rendered at all times.
    // The `min-h-4` prevents layout shift when the error message appears,
    // avoiding content below `TradeCurrencyError` from jumping.
    <div className="flex gap-1 items-start min-h-4">
      {tradeAmountErrorMessage && (
        <>
          <Image src={ExclamationBubbleRed} width={10} height={10} alt={tradeAmountErrorMessage} />
          <WebCaption12.Regular className="text-red">
            {tradeAmountErrorMessage}
          </WebCaption12.Regular>
        </>
      )}
    </div>
  );
};
